import type { DocContentPayload } from './types';

/** Encode bytes to base64 without overflowing the call stack. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000; // 32k chars per iteration
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Decode base64 to bytes. */
export function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Gzip-compress bytes using the native CompressionStream API. */
export async function gzip(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new CompressionStream('gzip'));
  const buffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(buffer);
}

/** Gunzip bytes using the native DecompressionStream API. */
export async function gunzip(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
  const buffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(buffer);
}

/**
 * Encode a Yjs binary payload into event content. Uses gzip when it
 * actually shrinks the payload (snapshots), raw base64 otherwise (tiny
 * incremental updates decompress worse than they compress).
 */
export async function encodeContent(bytes: Uint8Array): Promise<string> {
  if (bytes.length < 256) {
    return JSON.stringify({ v: 1, enc: 'raw', data: bytesToBase64(bytes) } satisfies DocContentPayload);
  }
  try {
    const compressed = await gzip(bytes);
    if (compressed.length < bytes.length) {
      return JSON.stringify({ v: 1, enc: 'gzip', data: bytesToBase64(compressed) } satisfies DocContentPayload);
    }
  } catch {
    // CompressionStream unavailable — fall back to raw.
  }
  return JSON.stringify({ v: 1, enc: 'raw', data: bytesToBase64(bytes) } satisfies DocContentPayload);
}

/** Decode event content into Yjs binary bytes. Returns undefined for malformed payloads. */
export async function decodeContent(content: string): Promise<Uint8Array | undefined> {
  let payload: DocContentPayload;
  try {
    payload = JSON.parse(content) as DocContentPayload;
  } catch {
    return undefined;
  }
  if (payload?.v !== 1 || (payload.enc !== 'raw' && payload.enc !== 'gzip') || typeof payload.data !== 'string') {
    return undefined;
  }
  try {
    const bytes = base64ToBytes(payload.data);
    if (payload.enc === 'gzip') {
      return await gunzip(bytes);
    }
    return bytes;
  } catch {
    return undefined;
  }
}

/** Total Unicode code points of a string (for display purposes). */
export function byteLength(bytes: Uint8Array): number {
  return bytes.length;
}
